// Lógica pura de comparar e igualar clientes: los textos de cada motivo y el resumen de un igualado.
import { formatDateTime, formatTime } from './dates';
import { plural } from './text';

// Por qué igualar no publica un update: son las reglas de publicar, las mismas que aplica Updates.
// `kind` lo manda el servidor; si llega uno que la pantalla no conoce, vale su `reason`.
export const blockedText = (item, source, target) => ({
    owner: `su prerrequisito ${item.prerequisite} es de ${item.prerequisite_owner}.`,
    ignored: `${target} tiene ignorado su prerrequisito ${item.prerequisite}.`,
    left_out: `su prerrequisito ${item.prerequisite} no está entre los elegidos.`,
    missing: `le falta su prerrequisito ${item.prerequisite}, que ${source} no tiene publicado.`,
}[item.kind] || item.reason);

// Lo mismo, en lo que entra en la etiqueta de un renglón; el nombre del prerrequisito queda en el tooltip.
export const blockedLabel = (item, target) => ({
    owner: `Su prerrequisito es de ${item.prerequisite_owner}`,
    ignored: `${target} tiene ignorado su prerrequisito`,
    missing: 'Le falta un prerrequisito',
}[item.kind] || 'No se puede publicar');

// "a o b", "a, b o c".
const orList = (parts) => (parts.length < 2 ? parts.join('') : `${parts.slice(0, -1).join(', ')} o ${parts[parts.length - 1]}`);

// Por qué no hay nada para igualar, dicho al lado del botón gris: según lo que le falta al cliente a igualar.
export const nothingToEqualize = (diff, source, target) => {
    if (diff.only_a.length === 0) return `No hay nada para igualar: ${target} ya tiene todo lo de ${source}.`;
    const has = (state) => diff.only_a.some(item => item.state === state);
    const why = [has('owner') && 'es de otro cliente', has('ignored') && 'lo tiene ignorado', has('blocked') && 'necesita un prerrequisito que no le puede llegar'].filter(Boolean);
    return `No hay nada para igualar: lo que le falta a ${target} ${orList(why)}.`;
};

// "A PCM le faltan 4 de TEC: 1 se le puede publicar.": la comparación, resumida en una frase.
export const missingSummary = (diff, source, target) => {
    const missing = diff.only_a.length, can = diff.publishable;
    if (missing === 0) return `A ${target} no le falta nada de ${source}.`;
    const how = can === 0 ? (missing === 1 ? 'no se le puede publicar' : 'ninguno se le puede publicar')
        : can === missing ? (missing === 1 ? 'se le puede publicar' : 'todos se le pueden publicar')
        : `${can} se le ${can === 1 ? 'puede' : 'pueden'} publicar`;
    return `A ${target} le ${missing === 1 ? 'falta' : 'faltan'} ${missing} de ${source}: ${how}.`;
};

// "hoy 14:59" o "8/10/2026 14:59": con el formato de fecha y hora de toda la aplicación (`lib/dates.js`).
export const whenText = (iso, now = new Date()) => {
    const date = new Date(iso);
    return date.toDateString() === now.toDateString() ? `hoy ${formatTime(date)}` : formatDateTime(date);
};

// Lo que pasó al igualar, para el aviso: qué se publicó y, de lo confirmado, qué no y por qué.
// `tone` sigue la regla de colores: verde si salió todo, ámbar si algo de lo confirmado no se publicó o si algo
// llegó desactualizado y hay que republicarlo, gris si no había nada para hacer.
export const equalizeOutcome = (result, target) => {
    const notPublished = result.not_published || [], errors = result.errors || [];
    const already = notPublished.filter(item => item.kind === 'already');
    const others = notPublished.filter(item => item.kind !== 'already');
    const published = result.published || [], outdated = result.outdated || [];
    const parts = [result.message];
    if (outdated.length > 0) parts.push(outdated.length === 1 ? '1 llegó desactualizado: hay que republicarlo.' : `${outdated.length} llegaron desactualizados: hay que republicarlos.`);
    if (result.copied > 0 && already.length > 0) parts.push(already.length === 1 ? `1 ya no hacía falta: ${target} ya lo tenía.` : `${already.length} ya no hacían falta: ${target} ya los tenía.`);
    if (others.length === 1) parts.push(`1 no se publicó: ${others[0].reason}`);
    if (others.length > 1) parts.push(`${others.length} no se publicaron.`);
    if (errors.length > 0) parts.push(`${plural(errors.length, 'falló', 'fallaron')} al copiarse.`);
    const details = [...errors.map(text => ({ text })), ...others.map(item => ({ text: `${item.name}: ${item.reason}` })), ...already.map(item => ({ text: `${item.name}: ${item.reason}` }))];
    const tone = result.copied === 0 ? 'info' : notPublished.length + errors.length + outdated.length > 0 ? 'warning' : 'success';
    return { text: parts.join(' '), details, published, outdated: outdated.length, tone };
};

// La comparación vista desde el otro cliente: con ella, los textos de un sentido sirven para el otro.
export const flipDiff = (diff) => ({ ...diff, only_a: diff.only_b, only_b: diff.only_a, publishable: diff.publishable_b, publishable_b: diff.publishable });

// Los resultados de igualar en los dos sentidos, como uno solo para el último igualado: el tono es el peor.
export const mergeOutcomes = (outcomes) => {
    const rank = { info: 0, success: 1, warning: 2 };
    return {
        text: outcomes.map(o => o.text).join(' '),
        details: outcomes.flatMap(o => o.details),
        published: outcomes.flatMap(o => o.published),
        outdated: outcomes.reduce((sum, o) => sum + o.outdated, 0),
        tone: outcomes.reduce((worst, o) => (rank[o.tone] > rank[worst] ? o.tone : worst), 'info'),
    };
};
